// Key-metrics data — shared by the on-page Key metrics tray (key-metrics.tsx)
// and the metric mode of the customization drawer (chart-customization-drawer),
// so both render the exact same KPIs from one source.
import { Change, type ChangeProps } from '@clickguard/design-system'
import { AnalyticsMetricId, MetricFormat } from 'services/user'
import {
	Clock,
	CursorClick01,
	CursorClick02,
	LogOut04,
	Target04,
	ShieldTick,
	SlashCircle01,
	AlertTriangle,
	AlertCircle,
	Lock01,
	CoinsHand,
	CurrencyDollar,
	SearchMd,
	Eye,
	File04,
	CheckDone01
} from './icons'

/** Display unit for a metric — drives the value formatting and is shown in the
 *  picker / drawer. `s` = seconds (rendered human-readable, e.g. "2m 14s"). */
export type MetricUnit = '$' | 'integer' | '%' | 's'

export interface KeyMetric {
	icon: JSX.Element
	/** Backend metric id — the key into the live /metrics response. */
	id: AnalyticsMetricId
	/** Value formatting (§3.7). Drives formatMetricValue for the live value. */
	format: MetricFormat
	label: string
	/** Placeholder value shown before the live data resolves. */
	value: string
	unit: MetricUnit
	trend: ChangeProps['trend']
	change: string
	changeType: ChangeProps['type']
	direction?: ChangeProps['direction']
	/** Plain-English explanation shown in the tile's info-icon tooltip. Copy is
	 *  reused from the Analytics page / Dashboard where it already exists, so the
	 *  same metric reads the same everywhere; the few metrics with no authored
	 *  copy anywhere carry a concise line written in the same voice. */
	tooltip: string
}

// The canonical metric set for the Key metrics CARDS section (Esteban's spec).
// Order is the spec order; the tray ships the first five as visible tiles and the
// rest are swap-in options in the on-card picker / customize drawer. Trend colour
// follows whether "up" is good for that metric (valid/engaged/savings up = green;
// bad/suspicious/bounce/CPC up = red; volume is shown neutral/brand).
//
// `tooltip` copy is lifted from where each metric is already explained — CTR /
// Bounce rate / Analyzed clicks / page views / page clicks from the Analytics
// metrics list, Engaged clicks from the Analytics funnel, Prevented ad waste /
// Blocked sources from the Dashboard. Bad traffic rate, Impressions, CPC, Avg
// clicks to convert, Avg time on website and the valid/bad/suspicious lines had
// no authored copy anywhere, so they carry concise lines in the same voice.
export const METRICS: KeyMetric[] = [
	{ icon: <CoinsHand />, id: 'prevented_ad_waste', format: 'currency0', label: 'Prevented ad waste', value: '$8,640', unit: '$', trend: 'positive', change: '12.0%', changeType: 'arrow', direction: 'up', tooltip: 'The amount of ad spend you saved by blocking invalid and fraudulent clicks, kept for real users instead.' },
	{ icon: <Lock01 />, id: 'blocked_sources', format: 'integer', label: 'Blocked sources', value: '1,284', unit: 'integer', trend: 'positive', change: '5.1%', changeType: 'arrow', direction: 'up', tooltip: 'Shows the number of harmful sources actively blocked from interacting with your ads again.' },
	{ icon: <AlertCircle />, id: 'bad_traffic_rate', format: 'percent1', label: 'Bad traffic rate', value: '11.4%', unit: '%', trend: 'positive', change: '1.8%', changeType: 'arrow', direction: 'down', tooltip: 'The share of analyzed ad clicks that ClickGuard flagged as invalid or fraudulent.' },
	{ icon: <SlashCircle01 />, id: 'bad_ad_clicks', format: 'integer', label: 'Bad ad clicks', value: '13,705', unit: 'integer', trend: 'negative', change: '14.3%', changeType: 'arrow', direction: 'up', tooltip: 'Clicks identified as invalid or fraudulent and filtered out by ClickGuard.' },
	{ icon: <AlertTriangle />, id: 'suspicious_ad_clicks', format: 'integer', label: 'Suspicious ad clicks', value: '4,210', unit: 'integer', trend: 'negative', change: '3.7%', changeType: 'arrow', direction: 'up', tooltip: 'Clicks showing risk signals that ClickGuard is monitoring but hasn’t blocked yet.' }
]

// Selectable on a tile but NOT shown as their own tile by default — the tray
// ships with the five above. These give the on-card metric picker / customize
// drawer the rest of the canonical pool to swap in.
export const EXTRA_METRICS: KeyMetric[] = [
	{ icon: <ShieldTick />, id: 'valid_ad_clicks', format: 'integer', label: 'Valid ad clicks', value: '102,295', unit: 'integer', trend: 'positive', change: '8.2%', changeType: 'trend', tooltip: 'Clicks from real, genuine visitors that passed ClickGuard’s fraud checks.' },
	{ icon: <SearchMd />, id: 'analyzed_ad_clicks', format: 'integer', label: 'Analyzed ad clicks', value: '120,000', unit: 'integer', trend: 'neutral', change: '28.84%', changeType: 'arrow', direction: 'down', tooltip: 'The total number of ad clicks received and analyzed, including both valid and invalid traffic.' },
	{ icon: <Target04 />, id: 'engaged_clicks', format: 'integer', label: 'Engaged clicks', value: '64,210', unit: 'integer', trend: 'positive', change: '6.9%', changeType: 'trend', tooltip: 'Clicks from visitors who showed meaningful interaction with your site (e.g. time spent, multiple page views, or events triggered).' },
	{ icon: <Eye />, id: 'impressions', format: 'integer', label: 'Impressions', value: '2,560,000', unit: 'integer', trend: 'neutral', change: '4.2%', changeType: 'arrow', direction: 'up', tooltip: 'The number of times your ads were shown to users.' },
	{ icon: <CursorClick02 />, id: 'ctr', format: 'percent1', label: 'CTR', value: '4.7%', unit: '%', trend: 'positive', change: '0.6%', changeType: 'trend', tooltip: 'Click-through rate (CTR) is the number of clicks your ad gets divided by how many times it’s shown (impressions). It shows how good your ad is at getting attention.' },
	{ icon: <CurrencyDollar />, id: 'cpc', format: 'currency2', label: 'CPC', value: '$1.84', unit: '$', trend: 'positive', change: '6.1%', changeType: 'arrow', direction: 'down', tooltip: 'Cost per click (CPC) is the average amount you pay each time someone clicks your ad.' },
	{ icon: <LogOut04 />, id: 'bounce_rate', format: 'percent1', label: 'Bounce rate', value: '38.2%', unit: '%', trend: 'negative', change: '2.1%', changeType: 'arrow', direction: 'up', tooltip: 'The percentage of visitors who left your site after viewing only one page, without any further interaction.' },
	{ icon: <File04 />, id: 'avg_page_views_per_visitor', format: 'decimal1', label: 'Avg. page views per visitor', value: '3', unit: 'integer', trend: 'positive', change: '0.4', changeType: 'arrow', direction: 'up', tooltip: 'The average number of pages viewed per visitor session.' },
	{ icon: <CursorClick01 />, id: 'avg_clicks_per_visitor', format: 'decimal1', label: 'Avg. page clicks per visitor', value: '5', unit: 'integer', trend: 'positive', change: '0.3', changeType: 'arrow', direction: 'up', tooltip: 'How many times, on average, each person clicks on your ads. It helps spot unusual patterns.' },
	{ icon: <CheckDone01 />, id: 'avg_clicks_to_convert', format: 'decimal1', label: 'Avg. clicks to convert', value: '4', unit: 'integer', trend: 'positive', change: '0.5', changeType: 'arrow', direction: 'down', tooltip: 'The average number of ad clicks it takes before a visitor completes a conversion.' },
	{ icon: <Clock />, id: 'avg_time_on_website', format: 'duration', label: 'Avg. time on website', value: '2m 14s', unit: 's', trend: 'positive', change: '11s', changeType: 'arrow', direction: 'up', tooltip: 'The average amount of time visitors spend on your site per session.' }
]

// Stable id per tile (labels are unique here). METRIC_IDS = the default tray tiles
// (the five core KPIs); METRIC_POOL = every metric a tile can be set to (core +
// extras). METRIC_BY_ID resolves either set, so a tile can render any pool metric.
export const METRIC_IDS = METRICS.map((m) => m.label)
export const METRIC_POOL = [...METRICS, ...EXTRA_METRICS].map((m) => m.label)
export const METRIC_BY_ID: Record<string, KeyMetric> = Object.fromEntries(
	[...METRICS, ...EXTRA_METRICS].map((m) => [m.label, m])
)

// Keyed by the backend metric id (`prevented_ad_waste`, …), unlike METRIC_BY_ID
// which is keyed by display label. Use this whenever the key comes from the API /
// a report snapshot (e.g. the tokenised render route), where labels aren't the key.
export const METRIC_BY_METRIC_ID: Record<string, KeyMetric> = Object.fromEntries(
	[...METRICS, ...EXTRA_METRICS].map((m) => [m.id, m])
)

// Metric-picker grouping (Gustavo's buckets): a 16-item flat list is a wall to
// scan, so the on-card Metric menu groups the pool by what the metric tells you
// about. Order here IS the render order. Every METRIC_POOL label must appear in
// exactly one group; `metricSections()` appends any stray (future) metric to a
// trailing untitled group so nothing silently disappears from the picker.
export const METRIC_GROUPS: { title: string; options: string[] }[] = [
	{ title: 'Traffic Protection', options: ['Prevented ad waste', 'Blocked sources', 'Bad traffic rate'] },
	{ title: 'Click Forensics', options: ['Analyzed ad clicks', 'Valid ad clicks', 'Suspicious ad clicks', 'Bad ad clicks'] },
	{ title: 'Performance', options: ['CTR', 'CPC', 'Impressions'] },
	{
		title: 'Behavior',
		options: [
			'Engaged clicks',
			'Bounce rate',
			'Avg. page views per visitor',
			'Avg. page clicks per visitor',
			'Avg. clicks to convert',
			'Avg. time on website'
		]
	}
]

// Partition a pool of metric labels into the groups above, preserving group and
// within-group order. Any label not covered by a group lands in a trailing
// untitled section so the picker never drops a metric.
export const metricSections = (pool: string[] = METRIC_POOL): { title?: string; options: string[] }[] => {
	const grouped = METRIC_GROUPS.map((g) => ({ title: g.title, options: g.options.filter((o) => pool.includes(o)) })).filter(
		(g) => g.options.length > 0
	)
	const covered = new Set(METRIC_GROUPS.flatMap((g) => g.options))
	const leftover = pool.filter((o) => !covered.has(o))
	return leftover.length ? [...grouped, { options: leftover }] : grouped
}

// For most metrics "up" vs "down" isn't obviously good or bad (more impressions?
// higher CPC? it depends), so a red/green delta quietly asserts a judgment we
// can't stand behind. We render those deltas in neutral BRAND purple instead —
// the arrow still shows the direction of change, the color just stops implying
// good/bad. The one exception is our core fraud metric, where lower is always
// better, so it keeps the semantic red (rose) / green (fell).
export const CORE_SIGNED_METRIC = 'Bad traffic rate'

// Change-pill props for a metric: purple (neutral) unless it's the core metric.
// Arrow direction always reflects the real movement (falls back from trend when
// the datum didn't pin one) so a purple delta still reads up or down.
export const metricChangeProps = (m: KeyMetric) => ({
	type: m.changeType,
	value: m.change,
	trend: (m.label === CORE_SIGNED_METRIC ? m.trend : 'neutral') as KeyMetric['trend'],
	direction: (m.direction ?? (m.trend === 'negative' ? 'down' : 'up')) as 'up' | 'down'
})

// The DS <Change/> trend pill for a metric, sized for the dense tile.
export const metricChange = (m: KeyMetric) => <Change size="sm" {...metricChangeProps(m)} />
